import { useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Form, Input, Button, Card, Select, Switch, Space, InputNumber, Typography, message, Spin, Upload } from 'antd';
import { ArrowLeftOutlined, SaveOutlined, ProjectOutlined, UploadOutlined, PlusOutlined } from '@ant-design/icons';
import { portfolioAPI, servicesAPI, categoriesAPI, uploadAPI } from '../services/api';

const { Title } = Typography;
const { TextArea } = Input;
const { Option } = Select;

export default function PortfolioForm() {
    const { id } = useParams();
    const navigate = useNavigate();
    const isEdit = !!id;
    const [form] = Form.useForm();
    const [loading, setLoading] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [services, setServices] = useState([]);
    const [categories, setCategories] = useState([]);
    const [uploading, setUploading] = useState(false);
    const [imageList, setImageList] = useState([]);

    const fetchServices = useCallback(async () => {
        try {
            const response = await servicesAPI.getAll();
            setServices(response.data || []);
        } catch (error) {
            console.error('Error fetching services:', error);
            message.error('Failed to fetch services');
        }
    }, []);

    const fetchCategories = useCallback(async () => {
        try {
            const response = await categoriesAPI.getActive();
            setCategories(response.data || []);
        } catch (error) {
            console.error('Error fetching categories:', error);
            message.error('Failed to fetch categories');
        }
    }, []);

    const fetchPortfolio = useCallback(async () => {
        if (!id) return;

        try {
            setLoading(true);
            const response = await portfolioAPI.getById(id);
            const data = response.data;
            form.setFieldsValue(data);

            // Set thumbnail image if exists
            if (data.thumbnail) {
                const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';
                const baseURL = API_URL.replace('/api', '');
                const fullImageUrl = `${baseURL}${data.thumbnail}`;

                setImageList([{
                    uid: '-1',
                    name: 'thumbnail',
                    status: 'done',
                    url: fullImageUrl,
                    thumbUrl: fullImageUrl,
                }]);
            }
        } catch (error) {
            console.error('Error fetching portfolio:', error);
            message.error('Failed to fetch portfolio');
        } finally {
            setLoading(false);
        }
    }, [id, form]);

    const handleUpload = async ({ file, onSuccess, onError }) => {
        try {
            setUploading(true);
            const response = await uploadAPI.single(file);
            const imageUrl = response.data.file.url;

            // Create full URL for preview (API_URL + image path)
            const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';
            const baseURL = API_URL.replace('/api', '');
            const fullImageUrl = `${baseURL}${imageUrl}`;

            // Set thumbnail value in form (store relative path)
            form.setFieldsValue({ thumbnail: imageUrl });

            // Update image list with full URL for preview
            setImageList([{
                uid: file.uid,
                name: file.name,
                status: 'done',
                url: fullImageUrl,
                thumbUrl: fullImageUrl,
            }]);

            onSuccess(response.data);
            message.success('Image uploaded successfully');
        } catch (error) {
            console.error('Upload error:', error);
            onError(error);
            message.error('Failed to upload image');
        } finally {
            setUploading(false);
        }
    };

    const handleRemoveImage = () => {
        setImageList([]);
        form.setFieldsValue({ thumbnail: null });
    };

    const generateSlug = (text) => {
        if (!text) return '';
        return text
            .toLowerCase()
            .replace(/[^a-z0-9\s-]/g, '') // Remove special characters
            .trim()
            .replace(/\s+/g, '-') // Replace spaces with hyphens
            .replace(/-+/g, '-'); // Replace multiple hyphens with single hyphen
    };

    const handleFormChange = (changedValues) => {
        // Auto-generate slug when title changes
        if (changedValues.title !== undefined) {
            const newSlug = generateSlug(changedValues.title);
            form.setFieldsValue({ slug: newSlug });
        }
    };

    useEffect(() => {
        fetchServices();
        fetchCategories();
    }, [fetchServices, fetchCategories]);

    useEffect(() => {
        if (isEdit) {
            fetchPortfolio();
        }
    }, [isEdit, fetchPortfolio]);

    const handleSubmit = async (values) => {
        setSubmitting(true);
        try {
            const dataToSubmit = {
                ...values,
                slug: values.slug || values.title.toLowerCase().replace(/[^a-z0-9]+/g, '-')
            };
            if (isEdit) {
                await portfolioAPI.update(id, dataToSubmit);
                message.success('Portfolio updated!');
            } else {
                await portfolioAPI.create(dataToSubmit);
                message.success('Portfolio created!');
            }
            navigate('/portfolio');
        } catch (error) {
            message.error('Error: ' + error.message);
        } finally {
            setSubmitting(false);
        }
    };

    if (loading) {
        return <div style={{ textAlign: 'center', padding: '50px' }}><Spin size="large" /></div>;
    }

    return (
        <div style={{ maxWidth: 900, margin: '0 auto' }}>
            <div style={{ marginBottom: 24 }}>
                <Button icon={<ArrowLeftOutlined />} onClick={() => navigate('/portfolio')} style={{ marginBottom: 16 }}>
                    Back to Portfolio
                </Button>
                <Title level={2}><ProjectOutlined /> {isEdit ? 'Edit' : 'Add'} Portfolio</Title>
            </div>

            <Card style={{ boxShadow: '0 2px 8px rgba(0,0,0,0.1)', borderRadius: '8px' }}>
                <Form
                    form={form}
                    layout="vertical"
                    onFinish={handleSubmit}
                    onValuesChange={handleFormChange}
                    initialValues={{ category: 'design', year: new Date().getFullYear(), isActive: true }}
                >
                    <Form.Item name="title" label="Project Title" rules={[{ required: true }]}>
                        <Input size="large" placeholder="Project name" />
                    </Form.Item>
                    <Form.Item name="slug" label="Slug"><Input size="large" /></Form.Item>
                    <Form.Item name="serviceId" label="Layanan (Service)">
                        <Select size="large" placeholder="Pilih layanan" allowClear>
                            {services.map(service => (
                                <Option key={service.id} value={service.id}>
                                    {service.title}
                                </Option>
                            ))}
                        </Select>
                    </Form.Item>
                    <Form.Item name="category" label="Category" rules={[{ required: true, message: 'Please select a category' }]}>
                        <Select size="large" placeholder="Pilih kategori">
                            {categories.map(category => (
                                <Option key={category.id} value={category.slug}>
                                    {category.name}
                                </Option>
                            ))}
                        </Select>
                    </Form.Item>
                    <Form.Item name="client" label="Client"><Input size="large" /></Form.Item>
                    <Form.Item name="location" label="Location"><Input size="large" /></Form.Item>
                    <Form.Item name="year" label="Year"><InputNumber size="large" style={{ width: '100%' }} /></Form.Item>
                    <Form.Item name="description" label="Description" rules={[{ required: true }]}>
                        <TextArea rows={4} />
                    </Form.Item>
                    <Form.Item name="challenge" label="Challenge"><TextArea rows={3} /></Form.Item>
                    <Form.Item name="solution" label="Solution"><TextArea rows={3} /></Form.Item>
                    <Form.Item name="result" label="Result"><TextArea rows={3} /></Form.Item>

                    {/* Hidden field for thumbnail URL */}
                    <Form.Item name="thumbnail" hidden>
                        <Input />
                    </Form.Item>

                    {/* Upload Image */}
                    <Form.Item label="Thumbnail Image">
                        <Upload
                            listType="picture-card"
                            fileList={imageList}
                            customRequest={handleUpload}
                            onRemove={handleRemoveImage}
                            maxCount={1}
                            accept="image/*"
                            disabled={uploading}
                        >
                            {imageList.length === 0 && (
                                <div>
                                    <PlusOutlined />
                                    <div style={{ marginTop: 8 }}>
                                        {uploading ? 'Uploading...' : 'Upload'}
                                    </div>
                                </div>
                            )}
                        </Upload>
                        <div style={{ color: '#666', fontSize: '12px', marginTop: '8px' }}>
                            Recommended size: 800x600px. Max file size: 5MB
                        </div>
                    </Form.Item>

                    <Form.Item name="isActive" label="Status" valuePropName="checked">
                        <Switch checkedChildren="Active" unCheckedChildren="Inactive" />
                    </Form.Item>
                    <Form.Item style={{ marginTop: 32 }}>
                        <Space style={{ width: '100%', justifyContent: 'flex-end' }}>
                            <Button size="large" onClick={() => navigate('/portfolio')} style={{ minWidth: 120 }}>Cancel</Button>
                            <Button type="primary" size="large" htmlType="submit" icon={<SaveOutlined />} loading={submitting} style={{ minWidth: 120, background: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)', border: 'none' }}>
                                {isEdit ? 'Update' : 'Create'} Portfolio
                            </Button>
                        </Space>
                    </Form.Item>
                </Form>
            </Card>
        </div>
    );
}
